import React, { useMemo } from 'react';
import {
  Avatar,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';

import makeStyles from '@mui/styles/makeStyles';
import classNames from 'classnames';
import { useAvatar } from 'modules/App/hooks/useAvatar';
import { useFormattedDuration } from 'modules/notes/common';
import { useScrollbarStyles } from 'modules/common';
import WorkHoursCell from './WorkHoursCell';
import MyTeamHoursLegend from './MyTeamHoursLegend';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    padding: theme.spacing(2),
    marginRight: theme.spacing(3),
    marginTop: theme.spacing(3),
    flexDirection: 'column',
    width: '100%'
  },

  nameHeader: {
    minWidth: theme.spacing(36),
    zIndex: '4',
    marginRight: theme.spacing(8)
  },
  nameHeaderDiv: {
    display: 'flex',
    justifyContent: 'space-between',
    color: theme.palette.text.secondary
  },
  otherHeader: {
    minWidth: theme.spacing(27),
    color: theme.palette.text.secondary,
    fontWeight: '600'
  },

  cell: {
    borderRight: '1px solid rgba(224, 224, 224, 1)'
  },
  dataCell: {
    fontSize: theme.spacing(3.75),
    padding: theme.spacing(0, 0.5)
  },
  totalCell: {
    color: theme.palette.text.secondary
  },
  sticky: {
    position: 'sticky',
    left: 0,
    background: 'white'
  },
  tableContainer: {
    maxHeight: theme.spacing(50)
  }
}));

const nameRowStyles = makeStyles((theme) => ({
  avatar: {
    backgroundColor: theme.palette.success.main,
    width: theme.spacing(4),
    height: theme.spacing(4),
    fontSize: theme.typography.body1.fontSize
  },
  nameCell: {
    display: 'flex',
    padding: theme.spacing(1, 2),
    justifyContent: 'space-between',
    zIndex: '3'
  },
  text: {
    marginLeft: theme.spacing(1),
    alignSelf: 'center'
  },

  span: {
    display: 'flex',
    justifyContent: 'space-around',
    marginRight: theme.spacing(8)
  },
  totalText: {
    color: theme.palette.text.secondary
  }
}));

type TableCellAvatarProp = {
  name: string;
  total: number;
};

const TableCellAvatar: React.FC<TableCellAvatarProp> = ({
  name,
  total
}: TableCellAvatarProp) => {
  const classes = nameRowStyles();
  const tableClasses = useStyles();
  const splitName = name.split(',');
  const lastName = splitName[1].trim();
  const firstName = splitName[0].trim();
  const { avatar } = useAvatar({
    firstName: firstName,
    lastName: lastName
  });
  const formattedDuration = useFormattedDuration({
    needsMask: true,
    duration: total ?? 0
  });
  return (
    <TableCell
      className={classNames(
        classes.nameCell,
        tableClasses.sticky,
        tableClasses.cell
      )}
    >
      <span className={classes.span}>
        <Avatar className={classes.avatar}>{avatar}</Avatar>
        <Typography variant="body1" className={classes.text}>
          {name}
        </Typography>
      </span>
      <span>
        <Typography variant="body2" className={classes.totalText}>
          {formattedDuration}
        </Typography>
      </span>
    </TableCell>
  );
};

const TeamTable: React.FC = () => {
  const classes = useStyles();
  function createData(
    name: string,
    total: number,
    Day1: number,
    Day2: number,
    Day3: number,
    Day4: number,
    Day5: number,
    Day6: number,
    Day7: number
  ) {
    return { name, total, data: [Day1, Day2, Day3, Day4, Day5, Day6, Day7] };
  }

  const rows = [
    createData('abc, xyz', 12000, 2000, 3000, 5000, 4000, 1000, 4567, 2000),
    createData('qwe, rfg', 14000, 3000, 5000, 7000, 4300, 5000, 2356, 3000),
    createData('asw, hjk', 15000, 4000, 3000, 3000, 6000, 6000, 1234, 4000),
    createData('iop, hjk', 16000, 6000, 2000, 5000, 4300, 8000, 3000, 5000),
    createData('rfg, hkjh', 17000, 7000, 4000, 5000, 3900, 4000, 5000, 6000),
    createData('abc, xyz', 12000, 2000, 3000, 5000, 4000, 1000, 6000, 7000),
    createData('qwe, rfg', 14000, 3000, 5000, 7000, 4300, 5000, 3467, 8000),
    createData('asw, hjk', 15000, 4000, 3000, 3000, 6000, 6000, 6543, 1000),
    createData('iop, hjk', 16000, 6000, 2000, 5000, 4300, 8000, 9873, 2340),
    createData('rfg, hkjh', 17000, 7000, 4000, 5000, 3900, 4000, 4567, 4500)
  ];

  const columns = useMemo(
    () => ['Day1', 'Day2', 'Day3', 'Day4', 'Day5', 'Day6', 'Day7'],
    []
  );

  const classesScrollbar = useScrollbarStyles();

  return (
    <Paper
      className={classes.container}
      elevation={3}
      data-testid={'my-team-container'}
    >
      <MyTeamHoursLegend />
      <TableContainer
        className={classNames(
          classes.tableContainer,
          classesScrollbar.scrollbar
        )}
        data-testid={'my-team-table-container'}
      >
        <Table
          aria-label="Team Table"
          stickyHeader
          size={'small'}
          data-testid={'my-team-table'}
        >
          <TableHead data-testid={'my-team-table-header'}>
            <TableRow>
              <TableCell
                className={classNames(
                  classes.nameHeader,
                  classes.sticky,
                  classes.cell
                )}
              >
                <div className={classes.nameHeaderDiv}>
                  <span>name</span>
                  <span>total</span>
                </div>
              </TableCell>

              {columns.map((i) => (
                <TableCell
                  className={classNames(classes.otherHeader, classes.cell)}
                  align="center"
                  key={i}
                >
                  {i}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody data-testid={'my-team-table-table-body'}>
            {rows.map((row) => (
              <TableRow key={row.name}>
                <TableCellAvatar name={row.name} total={row.total} />

                {row.data.map((i) => (
                  <TableCell
                    align="center"
                    className={classNames(classes.cell, classes.dataCell)}
                    key={i}
                  >
                    <WorkHoursCell
                      actualDuration={i}
                      scheduledDuration={4800}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};
export default TeamTable;
